import { useEffect, useState } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fechaSolo, fechaHora, money, colorRol, labelRol } from "@/lib/constants";
import { PeriodoTag } from "@/components/PeriodoTag";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import CommentBox from "@/components/CommentBox";
import CommentItem from "@/components/CommentItem";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, PackageOpen, MessageSquare, PackageCheck, CheckCircle2, AlertTriangle, Trash2, History, CalendarClock, StickyNote, Ban } from "lucide-react";

import FechaInput from "@/components/FechaInput";
const EST_BADGE = {
  "Pendiente": "bg-slate-100 text-slate-600",
  "En corte": "bg-amber-100 text-amber-800",
  "En pespunte": "bg-blue-100 text-blue-800",
  "En montado": "bg-violet-100 text-violet-800",
  "En adorno": "bg-pink-100 text-pink-800",
  "Terminado": "bg-emerald-100 text-emerald-800",
  "Entregado": "bg-slate-800 text-white",
};

export default function MuestraDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [estados, setEstados] = useState([]);
  const [mencionables, setMencionables] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showRechazo, setShowRechazo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [showEliminar, setShowEliminar] = useState(false);
  const [eliminado, setEliminado] = useState(false);
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [showCancelar, setShowCancelar] = useState(null);
  const [motivoCancel, setMotivoCancel] = useState("");
  const [showCancelarSol, setShowCancelarSol] = useState(false);
  const [motivoCancelSol, setMotivoCancelSol] = useState("");

  useEffect(() => { if (m && m.fecha_entrega) setFechaEntrega(m.fecha_entrega); }, [m?.fecha_entrega]);

  const loadHistorial = () => api.get(`/muestras/${id}/historial`).then((r) => setHistorial(r.data)).catch(() => {});
  const load = () => api.get(`/muestras/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
  useEffect(() => {
    load();
    loadHistorial();
    api.get("/muestras/productos").then((r) => setEstados(r.data.estados || [])).catch(() => {});
    api.get(`/muestras/${id}/usuarios-mencionables`).then((r) => setMencionables(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const puedeVer = user?.es_admin || user?.perms?.acciones?.ver_muestras || user?.perms?.acciones?.gestionar_muestras;
  const puedeGestionar = user?.es_admin || user?.perms?.acciones?.gestionar_muestras;
  const puedeComentar = user?.es_admin || !!user?.perms?.acciones?.comentar;
  if (m === false) return <Navigate to="/muestras" replace />;
  if (!m) return <div className="p-6 lg:p-8 max-w-3xl"><Skeleton className="h-96 rounded-xl" /></div>;
  const esSolicitanteView = user?.id === m.solicitante_id;
  if (user && !puedeVer && !esSolicitanteView) return <Navigate to="/" replace />;

  const cambiarEstado = async (pid, estado) => {
    try {
      await api.post(`/muestras/${id}/productos/${pid}/estado`, { estado });
      load();
      loadHistorial();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelarProducto = async () => {
    if (motivoCancel.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/muestras/${id}/productos/${showCancelar.id}/cancelar`, { motivo: motivoCancel.trim() });
      toast.success(r.data?.solicitud_cancelada ? "Producto cancelado — era el último, la solicitud quedó cancelada" : "Producto cancelado");
      setShowCancelar(null); setMotivoCancel("");
      load();
      loadHistorial();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const cancelarSolicitud = async () => {
    if (motivoCancelSol.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setBusy(true);
    try {
      await api.post(`/muestras/${id}/cancelar`, { motivo: motivoCancelSol.trim() });
      toast.success("Solicitud cancelada — se notificó al solicitante");
      setShowCancelarSol(false); setMotivoCancelSol("");
      load();
      loadHistorial();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const comentar = async (texto, menciones) => {
    setBusy(true);
    try {
      await api.post(`/muestras/${id}/comentarios`, { texto, menciones });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirmarEntrega = async () => {
    setBusy(true);
    try {
      await api.post(`/muestras/${id}/confirmar-entrega`);
      toast.success("Recepción confirmada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const guardarFechaEntrega = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/muestras/${id}/fecha-entrega`, { fecha_entrega: fechaEntrega });
      toast.success(r.data?.actualizacion ? "Fecha de entrega actualizada — se notificó al solicitante" : "Fecha de entrega guardada — se notificó al solicitante");
      load();
      loadHistorial();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const rechazarEntrega = async () => {
    setBusy(true);
    try {
      await api.post(`/muestras/${id}/rechazar-entrega`, { motivo });
      toast.success("Recepción rechazada");
      setShowRechazo(false); setMotivo("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const esSolicitante = user?.id === m.solicitante_id;
  const conf = m.confirmacion_entrega_estado;

  const eliminarMuestra = async () => {
    setBusy(true);
    try {
      await api.delete(`/muestras/${id}`);
      toast.success("Muestra eliminada");
      setEliminado(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (eliminado) return <Navigate to="/muestras" replace />;

  return (
    <div className="p-6 lg:p-8 max-w-3xl pb-16">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1.5 text-sm text-slate-500">
          <Link to="/muestras" className="inline-flex items-center gap-1 hover:text-slate-800 transition-colors"><ChevronLeft size={15} /> Muestras</Link>
          <span className="text-slate-300">/</span>
          <span className="font-medium text-slate-800">{m.folio}</span>
        </div>
        <div className="flex items-center gap-2">
          {puedeGestionar && !m.cancelada && !["pendiente_confirmacion", "confirmada"].includes(conf) && (
            <Button variant="outline" size="sm" onClick={() => setShowCancelarSol(true)} data-testid="cancelar-solicitud-btn" className="text-red-600 border-red-200 hover:bg-red-50">
              <Ban size={15} className="mr-1.5" /> Cancelar solicitud
            </Button>
          )}
          {user?.es_admin && (
            <Button variant="outline" size="sm" onClick={() => setShowEliminar(true)} data-testid="eliminar-muestra-btn" className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={15} className="mr-1.5" /> Eliminar
            </Button>
          )}
        </div>
      </div>

      {m.cancelada && (
        <Card data-testid="banner-solicitud-cancelada" className="border-red-200 bg-red-50 p-4 mb-6 flex items-start gap-2 text-sm text-red-800">
          <Ban size={18} className="text-red-600 mt-0.5 shrink-0" />
          <span>Solicitud cancelada por {m.cancelada_por_nombre}{m.cancelada_en ? ` · ${fechaHora(m.cancelada_en)}` : ""}. Motivo: {m.motivo_cancelacion_solicitud}</span>
        </Card>
      )}

      <div className="flex items-center gap-2 mb-6">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><PackageOpen size={18} /></div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">{m.folio}</h1>
      </div>

      <Card className="border-slate-200 shadow-sm p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide">Cliente</div><div className="font-semibold text-slate-800 mt-0.5">{m.cliente_nombre}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide">Fecha requerida</div><div className="font-semibold text-slate-800 mt-0.5">{m.fecha_requerida ? <>{fechaSolo(m.fecha_requerida)}<PeriodoTag date={m.fecha_requerida} /></> : "—"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide">Solicitante</div><div className="font-semibold text-slate-800 mt-0.5">{m.solicitante_nombre}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide flex items-center gap-1"><CalendarClock size={12} className="text-teal-500" /> Fecha de entrega</div><div className="font-semibold mt-0.5" data-testid="fecha-entrega-valor">{m.fecha_entrega ? <span className="text-teal-700">{fechaSolo(m.fecha_entrega)}<PeriodoTag date={m.fecha_entrega} /></span> : <span className="text-amber-600">Pendiente por capturar</span>}</div></div>
        </div>
      </Card>

      {m.comentario_creacion && (
        <Card className="border-amber-200 bg-amber-50/50 shadow-sm p-5 mb-6" data-testid="observacion-card">
          <div className="flex items-center gap-2 mb-1">
            <StickyNote size={16} className="text-amber-600" />
            <h3 className="font-head font-bold tracking-tight text-amber-900">Observación</h3>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="observacion-texto">{m.comentario_creacion}</p>
          <p className="text-[11px] text-slate-400 mt-1.5">Capturada al crear la solicitud.</p>
        </Card>
      )}

      {puedeGestionar && !m.cancelada && (
        <Card className="border-teal-200 bg-teal-50/40 shadow-sm p-5 mb-6" data-testid="fecha-entrega-card">
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock size={16} className="text-teal-600" />
            <h3 className="font-head font-bold tracking-tight text-teal-900">Fecha de Entrega</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Fecha real en que Desarrollo entregará la muestra (puede diferir de la requerida). Obligatoria antes de avanzar productos en el Tablero. Al guardar se notifica al solicitante.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Capturar fecha</label>
              <FechaInput value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} className="h-9 w-48 bg-white" data-testid="fecha-entrega-input" />
            </div>
            <Button onClick={guardarFechaEntrega} disabled={busy || !fechaEntrega || fechaEntrega === (m.fecha_entrega || "")} data-testid="fecha-entrega-guardar" className="bg-teal-600 hover:bg-teal-700 h-9">{m.fecha_entrega ? "Actualizar fecha" : "Guardar fecha"}</Button>
          </div>
        </Card>
      )}

      {conf === "pendiente_confirmacion" && esSolicitante && (
        <Card data-testid="banner-confirmar-entrega" className="border-cyan-200 bg-cyan-50 p-5 mb-6">
          <div className="flex items-start gap-3">
            <PackageCheck className="text-cyan-600 mt-0.5 shrink-0" size={22} />
            <div className="flex-1">
              <h3 className="font-head font-bold text-cyan-900">Confirma la recepción</h3>
              <p className="text-sm text-cyan-800 mt-1">Desarrollo marcó esta muestra como entregada. Confirma o rechaza la recepción. Si no respondes en 48 horas se confirmará automáticamente.</p>
              <div className="flex gap-2 mt-3">
                <Button data-testid="btn-confirmar-entrega" onClick={confirmarEntrega} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">Confirmar recepción</Button>
                <Button data-testid="btn-rechazar-entrega" variant="outline" onClick={() => setShowRechazo(true)} disabled={busy}>Rechazar</Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      {conf === "pendiente_confirmacion" && !esSolicitante && (
        <Card data-testid="banner-entrega-pendiente" className="border-cyan-200 bg-cyan-50 p-4 mb-6 flex items-center gap-2 text-sm text-cyan-800">
          <PackageCheck size={18} className="text-cyan-600" /> Entregada — esperando que {m.solicitante_nombre} confirme la recepción.
        </Card>
      )}
      {conf === "confirmada" && (
        <Card data-testid="banner-entrega-confirmada" className="border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 size={18} className="text-emerald-600" /> Recepción confirmada {m.confirmacion_auto ? "automáticamente (48h)" : `por ${m.confirmado_por}`}{m.fecha_confirmacion ? ` · ${fechaSolo(m.fecha_confirmacion)}` : ""}
        </Card>
      )}
      {conf === "rechazada" && (
        <Card data-testid="banner-entrega-rechazada" className="border-red-200 bg-red-50 p-4 mb-6 flex items-start gap-2 text-sm text-red-800">
          <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" /> <span>Recepción rechazada por {m.confirmado_por}. Motivo: {m.motivo_rechazo_entrega}</span>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-3.5 border-b border-slate-100"><h3 className="font-head font-bold tracking-tight">Productos</h3></div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100 bg-slate-50/60">
                <th className="px-5 py-2.5 font-semibold">Producto</th>
                <th className="px-3 py-2.5 font-semibold">SKU</th>
                <th className="px-3 py-2.5 font-semibold">Color</th>
                <th className="px-3 py-2.5 font-semibold">Talla</th>
                <th className="px-3 py-2.5 font-semibold">Cant.</th>
                <th className="px-3 py-2.5 font-semibold">Precio</th>
                <th className="px-3 py-2.5 font-semibold">Estado</th>
                {puedeGestionar && <th className="px-3 py-2.5 font-semibold text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {m.productos.map((p) => (
                <tr key={p.id} className={`border-b border-slate-50 ${p.cancelado ? "bg-red-50/40" : ""}`} data-testid={`det-prod-${p.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{p.nombre}</td>
                  <td className="px-3 py-3 text-slate-500 font-mono text-xs">{p.sku || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{p.color || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{p.talla || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{p.cantidad}</td>
                  <td className="px-3 py-3 text-slate-600">{p.precio != null ? money(p.precio) : "—"}</td>
                  <td className="px-3 py-3">
                    {p.cancelado ? (
                      <span className="text-xs px-2 py-1 rounded-full font-semibold bg-red-100 text-red-700 cursor-help" title={`Cancelado por ${p.cancelado_por_nombre || "—"}: ${p.motivo_cancelacion || ""}`} data-testid={`det-cancelado-${p.id}`}>Cancelado</span>
                    ) : puedeGestionar ? (
                      <Select value={p.estado} onValueChange={(v) => cambiarEstado(p.id, v)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`det-estado-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{estados.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${EST_BADGE[p.estado] || "bg-slate-100 text-slate-600"}`}>{p.estado}</span>
                    )}
                    {p.cancelado && p.motivo_cancelacion && <div className="text-[11px] text-red-500 mt-1 max-w-[220px]" data-testid={`det-motivo-cancel-${p.id}`}>Motivo: {p.motivo_cancelacion}</div>}
                  </td>
                  {puedeGestionar && (
                    <td className="px-3 py-3 text-right">
                      {!p.cancelado && (
                        <button onClick={() => { setShowCancelar(p); setMotivoCancel(""); }} data-testid={`cancelar-prod-${p.id}`} className="text-xs font-semibold text-red-600 hover:text-red-700 hover:underline">Cancelar</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm p-5">
        <h3 className="font-head font-bold tracking-tight flex items-center gap-2 mb-3"><MessageSquare size={16} /> Comentarios</h3>
        <div className="space-y-3 mb-4">
          {(m.comentarios || []).length === 0 && <p className="text-sm text-slate-400">Aún no hay comentarios.</p>}
          {(m.comentarios || []).map((c) => (
            <CommentItem key={c.id} c={c} testidPrefix="comentario" />
          ))}
        </div>
        {puedeComentar && <CommentBox users={mencionables} onSubmit={comentar} busy={busy} testidPrefix="comentario" />}
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden mt-6" data-testid="section-historial-muestra">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
          <History size={16} className="text-slate-400" />
          <h3 className="font-head font-bold tracking-tight">Historial de cambios</h3>
        </div>
        <div className="p-3 max-h-96 overflow-y-auto thin-scroll">
          {historial.length === 0 ? (
            <p className="text-sm text-slate-400 px-2 py-3">Sin actividad.</p>
          ) : (
            <div className="space-y-3">
              {historial.map((h) => (
                <div key={h.id} className="flex gap-3" data-testid={`hist-muestra-${h.id}`}>
                  <div className="mt-1 h-2 w-2 rounded-full bg-slate-300 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-700">{h.descripcion}</div>
                    <div className="text-[11px] text-slate-400">
                      {h.usuario_nombre} <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${colorRol(h.usuario_rol)}`}>{labelRol(h.usuario_rol)}</span> · {fechaHora(h.fecha)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Dialog open={!!showCancelar} onOpenChange={(o) => { if (!o) { setShowCancelar(null); setMotivoCancel(""); } }}>
        <DialogContent data-testid="cancelar-producto-modal">
          <DialogHeader>
            <DialogTitle>Cancelar producto{showCancelar ? `: ${showCancelar.nombre}` : ""}</DialogTitle>
            <DialogDescription>El producto no se borra: queda visible marcado como "Cancelado" y deja de contar para el % de avance de la muestra. Indica el motivo (mínimo 10 caracteres).</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="cancelar-producto-motivo" value={motivoCancel} onChange={(e) => setMotivoCancel(e.target.value)} placeholder="Motivo de la cancelación" rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelar(null); setMotivoCancel(""); }} disabled={busy}>Cerrar</Button>
            <Button data-testid="cancelar-producto-confirmar" disabled={motivoCancel.trim().length < 10 || busy} onClick={cancelarProducto} className="bg-red-600 hover:bg-red-700">Cancelar producto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelarSol} onOpenChange={(o) => { setShowCancelarSol(o); if (!o) setMotivoCancelSol(""); }}>
        <DialogContent data-testid="cancelar-solicitud-modal">
          <DialogHeader>
            <DialogTitle>Cancelar solicitud {m.folio}</DialogTitle>
            <DialogDescription>Se cancelan todos los productos pendientes y la solicitud se cierra. No se borra: queda visible como "Cancelada" y se avisa a {m.solicitante_nombre}. Indica el motivo (mínimo 10 caracteres).</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="cancelar-solicitud-motivo" value={motivoCancelSol} onChange={(e) => setMotivoCancelSol(e.target.value)} placeholder="Motivo de la cancelación" rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelarSol(false); setMotivoCancelSol(""); }} disabled={busy}>Cerrar</Button>
            <Button data-testid="cancelar-solicitud-confirmar" disabled={motivoCancelSol.trim().length < 10 || busy} onClick={cancelarSolicitud} className="bg-red-600 hover:bg-red-700">Cancelar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRechazo} onOpenChange={setShowRechazo}>
        <DialogContent data-testid="rechazo-entrega-modal">
          <DialogHeader>
            <DialogTitle>Rechazar recepción</DialogTitle>
            <DialogDescription>Indica el motivo del rechazo. Se notificará a Desarrollo.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="rechazo-entrega-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo del rechazo" rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRechazo(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="rechazo-entrega-confirmar" disabled={!motivo.trim() || busy} onClick={rechazarEntrega} className="bg-red-600 hover:bg-red-700">Rechazar recepción</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEliminar} onOpenChange={setShowEliminar}>
        <DialogContent data-testid="eliminar-muestra-modal">
          <DialogHeader>
            <DialogTitle>Eliminar muestra {m.folio}</DialogTitle>
            <DialogDescription>La muestra dejará de aparecer en el listado, el Tablero de productos y el Kanban. Esta acción se registra en el historial y no borra el registro de la base de datos.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEliminar(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="eliminar-muestra-confirmar" disabled={busy} onClick={eliminarMuestra} className="bg-red-600 hover:bg-red-700">Eliminar muestra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
